import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';

interface ShikakuItem {
  id: string;
  ShikakuName: string;
}

interface QuestionItem {
  id: string;
  SKref?: { id?: string };
}

interface ShikakuSummary {
  id: string;
  shikakuName: string;
  questionCount: number;
}

export default function ShikakuListPage() {
  const [items, setItems] = useState<ShikakuSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [shikakuSnapshot, questionsSnapshot] = await Promise.all([
          getDocs(collection(db, 'Shikaku')),
          getDocs(collection(db, 'Questions')),
        ]);

        const shikakuList = shikakuSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ShikakuName: docItem.data().ShikakuName || '',
        })) as ShikakuItem[];

        const questions = questionsSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        })) as QuestionItem[];

        const countMap = questions.reduce<Record<string, number>>((acc, question) => {
          const shikakuId = question.SKref?.id;
          if (!shikakuId) return acc;
          acc[shikakuId] = (acc[shikakuId] || 0) + 1;
          return acc;
        }, {});

        const summary = shikakuList
          .map((shikaku) => ({
            id: shikaku.id,
            shikakuName: shikaku.ShikakuName,
            questionCount: countMap[shikaku.id] || 0,
          }))
          .sort((a, b) => a.shikakuName.localeCompare(b.shikakuName, 'ja'));

        setItems(summary);
      } catch (error) {
        console.error('Error fetching shikaku summary:', error);
        alert('資格一覧の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">資格一覧</h1>
          <p className="text-gray-600">Shikakuごとの登録問題数を表示します</p>
        </div>
        <Link
          to="/admin/questions"
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          ← 問題管理に戻る
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">読み込み中...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">資格名(ShikakuName)</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-64">登録問題数</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="px-6 py-12 text-center text-gray-500">
                      資格データがありません
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-800">{item.shikakuName || item.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-800">{item.questionCount}件</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
